import { tw } from '@ctw/shared/utils/tailwind';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faCircleQuestion,
  faLock,
  faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

type Tone = 'caution' | 'error' | 'neutral';

const TONE_CLASSES: Record<
  Tone,
  {
    container: string;
    cardBorder: string;
    lineFill: string;
    icon: string;
    title: string;
    body: string;
  }
> = {
  caution: {
    container: 'border-caution-main/15 bg-caution-background',
    cardBorder: 'border-caution-badge',
    lineFill: 'bg-caution-badge',
    icon: 'text-caution-main',
    title: 'text-caution-text',
    body: 'text-caution-text/80',
  },
  error: {
    container: 'border-error-main/15 bg-error-background',
    cardBorder: 'border-error-badge',
    lineFill: 'bg-error-badge',
    icon: 'text-error-main',
    title: 'text-error-text',
    body: 'text-error-text/80',
  },
  neutral: {
    container: 'border-divider-main bg-background-hover',
    cardBorder: 'border-divider-main',
    lineFill: 'bg-background-subtle',
    icon: 'text-content-icon',
    title: 'text-content-main',
    body: 'text-content-subtle',
  },
};

interface PatientSummaryIllustrationPanelProps {
  tone: Tone;
  icon?: IconDefinition;
  title: string;
  body: string;
}

const PatientSummaryIllustrationPanel = ({
  tone,
  icon,
  title,
  body,
}: PatientSummaryIllustrationPanelProps) => {
  const classes = TONE_CLASSES[tone];

  return (
    <div className={tw`rounded-lg border px-6 py-12 ${classes.container}`}>
      <div className={tw`flex flex-col items-center gap-6`}>
        <div className={tw`relative h-[140px] w-[120px]`}>
          {/* Back card: +6deg */}
          <div
            className={tw`-translate-x-1/2 absolute top-2 left-1/2 h-[124px] w-[100px] rotate-6 rounded-lg border bg-white shadow-sm ${classes.cardBorder}`}
          >
            <div className={tw`flex flex-col gap-[8px] px-3.5 pt-5`}>
              <div className={tw`h-[5px] w-3/5 rounded-full ${classes.lineFill}`} />
              <div className={tw`h-[5px] w-2/5 rounded-full ${classes.lineFill}`} />
            </div>
          </div>
          {/* Middle card: -3deg */}
          <div
            className={tw`-translate-x-1/2 -rotate-3 absolute top-1 left-1/2 h-[124px] w-[100px] rounded-lg border bg-white shadow-sm ${classes.cardBorder}`}
          >
            <div className={tw`flex flex-col gap-[8px] px-3.5 pt-5`}>
              <div className={tw`h-[5px] w-1/2 rounded-full ${classes.lineFill}`} />
              <div className={tw`h-[5px] w-1/3 rounded-full ${classes.lineFill}`} />
            </div>
          </div>
          {/* Front card: centered icon, or text-content lines when no icon is provided */}
          <div
            className={tw`-translate-x-1/2 absolute top-0 left-1/2 h-[124px] w-[100px] rounded-lg border bg-white shadow-sm ${classes.cardBorder}`}
          >
            {icon ? (
              <div className={tw`flex h-full items-center justify-center pb-2`}>
                <FontAwesomeIcon icon={icon} className={tw`h-7 w-7 ${classes.icon}`} />
              </div>
            ) : (
              <div className={tw`flex flex-col gap-[6px] px-3.5 pt-5`}>
                <div className={tw`h-[5px] w-1/2 rounded-full ${classes.lineFill}`} />
                <div className={tw`h-[5px] w-1/3 rounded-full ${classes.lineFill}`} />
                <div className={tw`mt-2 h-[5px] w-4/5 rounded-full ${classes.lineFill}`} />
                <div className={tw`h-[5px] w-3/4 rounded-full ${classes.lineFill}`} />
                <div className={tw`h-[5px] w-4/5 rounded-full ${classes.lineFill}`} />
              </div>
            )}
          </div>
        </div>
        <div className={tw`flex max-w-[40ch] flex-col items-center gap-1 text-center`}>
          <p className={tw`font-medium text-sm ${classes.title}`}>{title}</p>
          <p className={tw`text-sm ${classes.body}`}>{body}</p>
        </div>
      </div>
    </div>
  );
};

export const PatientSummaryLimitReached = () => (
  <PatientSummaryIllustrationPanel
    tone="error"
    icon={faLock}
    title="Monthly summary limit reached"
    body="You've used all your included patient summaries this month. Contact your account team for expanded access."
  />
);

export const PatientSummaryInsufficientData = () => (
  <PatientSummaryIllustrationPanel
    tone="caution"
    icon={faCircleQuestion}
    title="Patient data hasn't finished processing"
    body="There isn't enough chart data to generate a summary yet. This usually means the patient history job is still running or hasn't started. Please check back shortly."
  />
);

export const PatientSummaryNoSummaryYet = () => (
  <PatientSummaryIllustrationPanel
    tone="neutral"
    title="No summary generated yet"
    body="A summary hasn't been requested for this patient yet."
  />
);

export const PatientSummaryError = () => (
  <PatientSummaryIllustrationPanel
    tone="error"
    icon={faTriangleExclamation}
    title="Something went wrong"
    body="We couldn't generate the summary. Try again or contact support if the problem persists."
  />
);

const SKELETON_LINE = 'h-3 animate-pulse rounded-md bg-content-subtle/10';

export const PatientSummaryGeneratingSkeleton = () => (
  <output className={tw`flex flex-col gap-3`} aria-label="Generating patient summary">
    {[0, 1, 2].map((cardIndex) => (
      <div
        key={cardIndex}
        className={tw`flex flex-col gap-2 rounded-lg border border-divider-main bg-white p-4 shadow-sm`}
      >
        <div className={tw`${SKELETON_LINE} mb-2 w-1/3`} />
        <div className={tw`${SKELETON_LINE} w-full`} />
        <div className={tw`${SKELETON_LINE} w-11/12`} />
        <div className={tw`${SKELETON_LINE} w-3/4`} />
      </div>
    ))}
  </output>
);
